import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { LineChart } from "@mui/x-charts/LineChart";
import React, { useEffect, useState } from "react";
import useWellAnalysis from "../hooks/useWellAnalysis";
import { Box, Typography } from "@mui/material";
const COLOR_MAP = {
    "Oil Mix": "#a8a8a8",
    "F": "#CF372C",
    "P50 Oil Mix": "#7F7F7F",
    "P50 Refrac": "#2E7D32",
    "Frac": "#CF372C",
    "Frac Forecast": "#CF372C",
};
const ProductionLineChart = ({ studyUuid, wellApi, isCumulative, isOverlaid }) => {
    const [series, setSeries] = useState([]);
    const [xAxis, setXAxis] = useState([]);
    const [yAxis, setYAxis] = useState([]);
    const [loading, setLoading] = useState(false);
    const { fetchChartData } = useWellAnalysis();
    const getYAxisLabel = () => {
        let ret = '';
        if (!isCumulative && !isOverlaid || !isCumulative && isOverlaid) {
            ret = 'Boe/d';
        }
        else if (isCumulative && !isOverlaid || isCumulative && isOverlaid) {
            ret = 'MBoe';
        }
        return ret;
    };
    const getXAxisLabel = () => {
        let ret = '';
        if (!isCumulative && !isOverlaid || isCumulative && !isOverlaid) {
            ret = 'Year';
        }
        else if (!isCumulative && isOverlaid || isCumulative && isOverlaid) {
            ret = 'Month';
        }
        return ret;
    };
    const getSeriesColor = (label) => {
        return COLOR_MAP[label] || "#000000";
    };
    const getSeriesData = async ({ studyUuid, wellApi, isCumulative, isOverlaid }) => {
        if (!studyUuid || !wellApi) {
            return;
        }
        setLoading(true);
        try {
            const seriesResponse = await fetchChartData(studyUuid, wellApi, isCumulative, isOverlaid);
            seriesResponse.xAxis[0].label = getXAxisLabel();
            seriesResponse.yAxis[0].label = getYAxisLabel();
            if ((!isOverlaid && !isCumulative) || (isCumulative && !isOverlaid)) {
                seriesResponse.xAxis[0].valueFormatter = (value) => {
                    const year = new Date(value).getFullYear();
                    return isNaN(year) ? value : year.toString();
                };
            }
            if (isOverlaid) {
                for (let i = 0; i < seriesResponse.series.length; i++) {
                    seriesResponse.series[i].data = seriesResponse.series[i].data?.slice(0, 59);
                }
                seriesResponse.xAxis[0].data = seriesResponse.xAxis[0].data.slice(0, 59);
                seriesResponse.xAxis[0].tickInterval = [10, 20, 30, 40, 50, 60];
            }
            setSeries(seriesResponse.series.map((s) => ({
                ...s,
                color: getSeriesColor(s.label),
                showMark: false,
                baseline: s.yAxisId == "oil_mix_axis" ? "min" : 0,
            })));
            setXAxis(seriesResponse.xAxis);
            setYAxis(seriesResponse.yAxis);
        }
        catch (error) {
            console.log("Error retrieving chart data: ", studyUuid, wellApi, isCumulative);
        }
        finally {
            setLoading(false);
        }
    };
    const getChartTitle = (isCumulative, isOverlaid) => {
        const subscriptT0 = "T₀";
        if (isCumulative) {
            return isOverlaid
                ? `Cumulative Production (Overlaid ${subscriptT0})`
                : "Cumulative Production";
        }
        else {
            return isOverlaid
                ? `Daily Production (Overlaid ${subscriptT0})`
                : "Daily Production";
        }
    };
    useEffect(() => {
        getSeriesData({ studyUuid, wellApi, isCumulative, isOverlaid });
    }, [studyUuid, wellApi]);
    if (!series) {
        return (_jsx(Typography, { variant: "h6", children: "No data available" }));
    }
    const rightAxis = yAxis[1] ? yAxis[1].id : null;
    return (_jsx(_Fragment, { children: _jsxs(Box, { sx: {
                aspectRatio: "4 / 3",
                borderRadius: "10px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                height: "100%", width: "100%",
            }, children: [_jsx(Typography, { sx: { marginTop: '10px', fontWeight: 'bold' }, variant: "h6", children: getChartTitle(isCumulative, isOverlaid) }), _jsx(Box, { sx: { height: "90%", width: "90%", margin: 'auto' }, children: _jsx(LineChart, { loading: loading, sx: {
                            '.MuiLineElement-root': {
                                strokeWidth: '2',
                            },
                            '.MuiLineElement-series-fracForecast': {
                                strokeDasharray: '5 5',
                            },
                        }, grid: {
                            horizontal: true,
                            vertical: false,
                        }, series: series, xAxis: xAxis, yAxis: yAxis, rightAxis: rightAxis }) })] }) }));
};
export default ProductionLineChart;
